import { getAuthHeader } from './api'

/**
 * Servicio para exportación de datos en CSV
 * (usa fetch directo, no la instancia de axios, para poder leer la
 * respuesta como blob de descarga — ver getAuthHeader en services/api.ts)
 */

export interface ExportFilters {
  startDate?: string;
  endDate?: string;
  source?: string;
  type?: string;
  flow?: string;
}

/**
 * Descarga un archivo CSV desde el backend
 */
const downloadCSV = async (endpoint: string, filters: ExportFilters, filename: string) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.source && filters.source !== 'all') params.append('source', filters.source);
    if (filters.type && filters.type !== 'all') params.append('type', filters.type);
    if (filters.flow && filters.flow !== 'all') params.append('flow', filters.flow);
    
    const url = `/api${endpoint}?${params.toString()}`;
    
    console.log('🔍 Descargando CSV desde:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'text/csv',
        ...getAuthHeader(),
      },
    });
    
    console.log('📦 Respuesta del servidor:', response.status, response.statusText);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Obtener el blob de la respuesta
    const blob = await response.blob();
    console.log('📄 Blob creado:', blob.size, 'bytes, tipo:', blob.type);
    
    // Crear URL de descarga y descargar
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    console.log('✅ Archivo descargado exitosamente:', filename);
    return { success: true, message: 'Archivo descargado exitosamente' };
  } catch (error) {
    console.error('❌ Error downloading CSV:', error);
    return { 
      success: false, 
      message: 'Error al descargar el archivo CSV',
      error: error instanceof Error ? error.message : 'Error desconocido'
    };
  }
};

/**
 * Genera un nombre de archivo descriptivo basado en los filtros aplicados
 */
const generateFilename = (viewType: string, filters: ExportFilters): string => {
  const date = new Date().toISOString().split('T')[0];
  const parts = [`dashboard_${viewType}`, date];
  
  // Agregar información de filtros si están presentes
  if (filters.startDate && filters.endDate) {
    parts.push(`${filters.startDate}_to_${filters.endDate}`);
  }
  
  if (filters.type && filters.type !== 'all') {
    parts.push(`tipo_${filters.type.toLowerCase()}`);
  }
  
  if (filters.source && filters.source !== 'all') {
    parts.push(`fuente_${filters.source.toLowerCase()}`);
  }
  
  if (filters.flow && filters.flow !== 'all') {
    parts.push(`flujo_${filters.flow.toLowerCase()}`);
  }
  
  return `${parts.join('_')}.csv`;
};

/**
 * Exporta datos de campañas en CSV
 */
export const exportCampaignsCSV = async (filters: ExportFilters) => {
  const filename = generateFilename('campañas', filters);
  return await downloadCSV('/export/campaigns', filters, filename);
};

/**
 * Exporta datos de revenue por día en CSV
 */
export const exportRevenuePerDayCSV = async (filters: ExportFilters) => {
  const filename = generateFilename('revenue_por_dia', filters);
  return await downloadCSV('/export/revenue-per-day', filters, filename);
};

/**
 * Exporta datos de overview general en CSV
 */
export const exportOverviewCSV = async (filters: ExportFilters) => {
  const filename = generateFilename('overview_general', filters);
  return await downloadCSV('/export/overview', filters, filename);
};

/**
 * Hook personalizado para manejar exportaciones CSV
 */
export const useCSVExport = () => {
  const exportData = async (type: 'campaigns' | 'revenue-per-day' | 'overview', filters: ExportFilters) => {
    try {
      let result;
      
      switch (type) {
        case 'campaigns':
          result = await exportCampaignsCSV(filters);
          break;
        case 'revenue-per-day':
          result = await exportRevenuePerDayCSV(filters);
          break;
        case 'overview':
          result = await exportOverviewCSV(filters);
          break;
        default:
          throw new Error('Tipo de exportación no válido');
      }
      
      if (result.success) {
        console.log('✅ Exportación exitosa:', result.message);
        return { success: true, message: result.message };
      } else {
        console.error('❌ Error en exportación:', result.message);
        return { success: false, message: result.message };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      console.error('❌ Error en exportación:', errorMessage);
      return { success: false, message: errorMessage };
    }
  };
  
  return { exportData };
};
